import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiSearchLine, RiCornerDownLeftLine } from '@remixicon/react';
import { api } from '@/lib/api/client';
import { useMallScopeStore } from '@/stores/mallScopeStore';
import type { SearchGroupDto } from '@/lib/api/queries';

/**
 * GENEL ARAMA (Ctrl/⌘ + K).
 *
 * TAMAMEN KLAVYEDEN KULLANILABİLİR OLMASI BİR SÜS DEĞİL.
 *
 * AVM yönetiminde en sık yapılan iş "şu markayı bul, şu üniteye git".
 * Fare ile menüde gezinmek bunu 4-5 tıklamaya çıkarır. Ok tuşları ve
 * Enter ile çalışan bir arama, günde onlarca kez yapılan bu işi tek
 * hamleye indirir.
 *
 * SONUÇLAR SUNUCUDAN GELİR, İSTEMCİDE FİLTRELENMEZ: bir AVM'de 60 ünite,
 * 220 kiracı, binlerce fatura var. Hepsini indirip istemcide aramak hem
 * yavaş hem de yetki sınırlarını istemciye emanet etmek olurdu.
 */

/** Bir tuş vuruşundan sonra beklenen süre. */
const DEBOUNCE_MS = 250;

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [groups, setGroups] = useState<SearchGroupDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const mallId = useMallScopeStore((s) => s.mallId);

  /*
    İKİ HARFTEN KISA TERİMDE SONUÇ GÖSTERİLMEZ.

    Bu bir GÖRÜNÜRLÜK kuralıdır, bir state değişikliği değil: kullanıcı
    "kah" yazıp bir harf sildiğinde önceki sonuçlar bir an ekranda kalmasın
    diye state'i temizlemek gerekmiyor — render'da süzmek yeterli ve bir
    render turu daha ucuz.
  */
  const visibleGroups = term.trim().length < 2 ? [] : groups;

  // Düz liste: ok tuşlarıyla gezinmek için grupları tek diziye açıyoruz.
  const flat = visibleGroups.flatMap((g) => g.items.map((item) => ({ ...item, group: g.label })));

  // --- Kısayol -------------------------------------------------------------
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  /*
    ODAKLAMA EFEKTTE, SIFIRLAMA EYLEMDE.

    Önce ikisi de aynı efektteydi: panel kapanınca efekt state'i temizliyordu.
    Odak vermek bir DOM YAN ETKİSİdir (efektin yeri orası), sıfırlama ise
    kullanıcının "kapat" eyleminin bir parçasıdır. Efektten state yazmak
    fazladan bir render turu üretir ve React kuralı bunu reddeder.
  */
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(id);
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    setTerm('');
    setGroups([]);
    setActiveIndex(0);
  }, []);

  // --- Arama ---------------------------------------------------------------
  useEffect(() => {
    // Kısa terimde istek atılmaz; sonuçların gizlenmesi TÜRETİLEREK yapılır
    // (aşağıdaki `visibleGroups`), efektten state yazılarak değil.
    if (term.trim().length < 2) return;

    /*
      HER İSTEK KENDİNDEN SONRAKİ TARAFINDAN İPTAL EDİLİR.

      Kullanıcı "kah" yazarken üç istek gider. Ağ gecikmesi eşit değildir;
      "ka" isteği "kah" isteğinden SONRA dönebilir ve ekranda yanlış sonuç
      kalır. AbortController bunu yapısal olarak imkânsız kılar.
    */
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const result = await api.get<{ groups: SearchGroupDto[] }>('/workspace/search', {
          query: { q: term.trim() },
          signal: controller.signal,
        });
        setGroups(result.groups);
        setActiveIndex(0);
      } catch {
        // İptal edilen istek hata değildir; kullanıcı yazmaya devam ediyor.
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
    // mallId bağımlılıkta: AVM değişince sonuçlar da değişmeli.
  }, [term, mallId]);

  const go = (to: string) => {
    close();
    navigate(to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (flat.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flat.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = flat[activeIndex];
      if (target) go(target.to);
    }
  };

  return (
    <>
      <button
        type="button"
        className="header-search header-search--button ms-2"
        onClick={() => setOpen(true)}
        aria-label="Genel arama (Ctrl+K)"
      >
        <span className="header-search__placeholder">Kiracı, ünite, fatura ara...</span>
        <kbd className="header-search__kbd">Ctrl K</kbd>
        <RiSearchLine className="header-search__icon" size={16} aria-hidden="true" />
      </button>

      {open && (
        <div className="cmdk" role="presentation" onMouseDown={close}>
          <div
            className="cmdk__panel"
            role="dialog"
            aria-label="Genel arama"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="cmdk__input-row">
              <RiSearchLine size={18} aria-hidden="true" className="text-muted" />
              <input
                ref={inputRef}
                className="cmdk__input"
                placeholder="Kiracı, ünite, sözleşme, fatura veya talep ara..."
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={onKeyDown}
                aria-label="Arama terimi"
                autoComplete="off"
              />
              <kbd className="header-search__kbd">ESC</kbd>
            </div>

            <div className="cmdk__results">
              {term.trim().length < 2 && (
                <div className="cmdk__hint">
                  En az iki harf yazın.
                  <div className="fs-11 mt-1">
                    Ünite kodu, marka adı, vergi numarası, fatura numarası veya talep numarası
                    ile arayabilirsiniz.
                  </div>
                </div>
              )}

              {term.trim().length >= 2 && loading && flat.length === 0 && (
                <div className="cmdk__hint">Aranıyor…</div>
              )}

              {term.trim().length >= 2 && !loading && flat.length === 0 && (
                <div className="cmdk__hint">
                  <strong>{term}</strong> için sonuç bulunamadı.
                </div>
              )}

              {visibleGroups.map((group) => (
                <div key={group.key} className="cmdk__group">
                  <div className="cmdk__group-label">{group.label}</div>
                  {group.items.map((item) => {
                    const index = flat.findIndex((f) => f.id === item.id && f.to === item.to);
                    const isActive = index === activeIndex;

                    return (
                      <button
                        key={`${group.key}-${item.id}`}
                        type="button"
                        className={`cmdk__item${isActive ? ' is-active' : ''}`}
                        onClick={() => go(item.to)}
                        onMouseEnter={() => setActiveIndex(index)}
                      >
                        <span className="cmdk__item-title">{item.title}</span>
                        <span className="cmdk__item-subtitle">{item.subtitle}</span>
                        {isActive && (
                          <RiCornerDownLeftLine
                            size={14}
                            className="cmdk__item-enter"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {/*
              OK İŞARETLERİ YERİNE KELİME.

              Poppins'in latin-ext alt kümesinde ↑ ↓ glifleri yok; tarayıcı
              yedek yazı tipine düşemediğinde tuş rozetleri BOŞ kutular
              olarak çıkıyordu. İpucu satırının kendisi okunamıyorsa
              varlığının anlamı kalmaz.
            */}
            <div className="cmdk__footer">
              <span>
                <kbd>Yön tuşları</kbd> gezin
              </span>
              <span>
                <kbd>Enter</kbd> aç
              </span>
              <span>
                <kbd>Esc</kbd> kapat
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
